import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { knowledgeApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Loader2, BookOpen, Search } from 'lucide-react';

export default function KnowledgeBrowsePage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get('q') || '');
  const [activeCat, setActiveCat] = useState(params.get('cat') || '');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      knowledgeApi.listArticles(activeCat ? { category_id: activeCat } : {}),
      knowledgeApi.listCategories(),
    ]).then(([a, c]) => { setArticles(a.data); setCats(c.data); })
      .finally(() => setLoading(false));
  }, [activeCat]);

  const filtered = articles.filter(a =>
    !q.trim() || a.title.toLowerCase().includes(q.toLowerCase()) ||
    (a.summary || '').toLowerCase().includes(q.toLowerCase()) ||
    (a.tags || []).some(t => t.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="knowledge-browse-page">
      <div>
        <h1 className="text-3xl font-bold text-[#0A2342] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <BookOpen className="w-7 h-7 text-[#0E7490]" />Knowledge Base
        </h1>
        <p className="text-[#64748B] mt-1">Documentation, how-to articles, and reference material.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="pl-9" data-testid="kb-search-input" />
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => { setActiveCat(''); setParams({}); }} className={`px-3 py-1.5 rounded-full text-xs ${!activeCat ? 'bg-[#0E7490] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'}`} data-testid="kb-cat-all">All</button>
        {cats.map(c => (
          <button key={c.id} onClick={() => { setActiveCat(c.id); setParams({ cat: c.id }); }} className={`px-3 py-1.5 rounded-full text-xs ${activeCat === c.id ? 'bg-[#0E7490] text-white' : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'}`} data-testid={`kb-cat-${c.id}`}>
            {c.name} <span className="opacity-70">({c.article_count})</span>
          </button>
        ))}
      </div>

      {loading ? <Loader2 className="w-8 h-8 animate-spin text-[#0E7490]" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(a => (
            <Card key={a.id} className="careiq-card overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/knowledge/${a.slug || a.id}`)} data-testid={`kb-article-${a.id}`}>
              {a.featured_image_url ? (
                <img src={a.featured_image_url} alt="" className="w-full h-36 object-cover" />
              ) : (
                <div className="w-full h-36 bg-gradient-to-br from-[#0E7490]/10 to-[#0A2342]/10 flex items-center justify-center">
                  <BookOpen className="w-10 h-10 text-[#94A3B8]" />
                </div>
              )}
              <CardContent className="p-4 space-y-2">
                <h3 className="font-semibold text-[#0A2342] line-clamp-2">{a.title}</h3>
                <p className="text-xs text-[#64748B] line-clamp-3">{a.summary}</p>
                <div className="flex items-center justify-between text-[10px] text-[#94A3B8]">
                  <span>{a.author_name}</span>
                  <span>{a.view_count} views</span>
                </div>
                {(a.tags || []).slice(0, 3).map(t => (
                  <Badge key={t} className="bg-[#F1F5F9] text-[#64748B] mr-1">{t}</Badge>
                ))}
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && <Card className="careiq-card col-span-full"><CardContent className="p-10 text-center text-[#64748B]">No articles found.</CardContent></Card>}
        </div>
      )}
    </div>
  );
}
